import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isPlaceholderValue, isRejectedUserValue } from "@/utils/sanitizeValue";
import { displayAttributeValue } from "@/utils/attributeValue";
import {
  type AttributeSourceKind,
  pickByPrecedence,
} from "@/domains/buildings/constants/sourcePrecedence";
import { rejectionStillApplies } from "./rejectionState";

/**
 * Attribute names from the building data model that we display in the header.
 * These exist in `data.building_extractor_findings` (AI-extracted),
 * `data.api_building_data` (EHR/API-sourced), and `data.user_data` (user-entered).
 *
 * Note: registry_code is deliberately excluded — it is always sourced directly
 * from `public.buildings.registry_code` (the user-entered value at building
 * creation, updated on edit) and must never be overwritten by AI extraction.
 */
const HEADER_ATTRIBUTES = ["net_area", "floors", "heating_source"] as const;
type HeaderAttribute = (typeof HEADER_ATTRIBUTES)[number];
function isHeaderAttribute(key: string): key is HeaderAttribute {
  return (HEADER_ATTRIBUTES as readonly string[]).includes(key);
}

/** RPC row shapes the merge reads (the custom RPCs aren't in the generated
 * Supabase types). Only the consumed fields are declared. */
interface FindingRow {
  attribute_nm: string;
  extracted_value: string;
  source_filenames: string[] | null;
  modified_at?: string | null;
}
interface ApiRow {
  attribute_key: string;
  attribute_value: string;
  source_system: string | null;
  created_at?: string | null;
}
interface IfcFindingRow {
  attribute_nm: string;
  extracted_value: string;
  ifc_filename: string | null;
  modified_at?: string | null;
}
interface UserRow {
  attribute_key: string;
  attribute_value: string | null;
  edited_by_name: string | null;
  edited_by: number | null;
  edited_by_avatar_url: string | null;
  edited_by_color: string | null;
  created_at: string | null;
}

/**
 * The pure merge uses an empty string as its internal explicit-clear marker.
 * Normalize persisted rejection rows at the query boundary so SQL NULL and
 * the legacy marker map to that representation while historic blank clears
 * remain ordinary nulls.
 */
function normalizeHeaderUserRows(rows: UserRow[] | null): UserRow[] | null {
  if (!rows) return null;
  return rows.map((row) => ({
    ...row,
    attribute_value: isRejectedUserValue(row.attribute_value)
      ? ""
      : row.attribute_value?.trim() === ""
        ? null
        : row.attribute_value,
  }));
}

function machineUpdateTimes(
  key: HeaderAttribute,
  rows: {
    findings: FindingRow[] | null;
    api: ApiRow[] | null;
    ifc: IfcFindingRow[] | null;
  }
): Array<string | null | undefined> {
  return [
    ...(rows.findings ?? [])
      .filter((row) => row.attribute_nm === key)
      .map((row) => row.modified_at),
    ...(rows.api ?? []).filter((row) => row.attribute_key === key).map((row) => row.created_at),
    ...(rows.ifc ?? []).filter((row) => row.attribute_nm === key).map((row) => row.modified_at),
  ];
}

export interface AttributeSourceUser {
  id: number;
  name: string;
  avatarUrl: string | null;
  color: string | null;
  /** When the edit was made (user_data.created_at) — shown in the field-edit popover. */
  editedAt: string | null;
}

export interface AttributeField {
  value: string | null;
  source: string | null;
  // Set when `source` starts with "Edited by:" / "Confirmed by:" so the
  // BuildingHeader tooltip can render the editor's avatar + name.
  sourceUser?: AttributeSourceUser | null;
}

export interface BuildingHeaderAttributes {
  netArea: AttributeField;
  floors: AttributeField;
  heatingSource: AttributeField;
}

/**
 * Fetches building attributes from all four data sources and merges them.
 * Each source contributes a candidate per attribute; the winner is resolved by
 * the shared SOURCE_PRECEDENCE (user > ifc > ehr > extractor, WHA-3688) so the
 * header and the building-data sidebar can never disagree on the ranking and
 * flipping it is a one-line change. User edits still win; among machine sources
 * the BIM model outranks EHR which outranks document extraction.
 */
/**
 * Pure merge of the four data sources into the header's per-attribute fields.
 * Each source contributes at most one candidate per attribute (keep-first — the
 * RPCs return rows best-first, and get_ifc_findings now orders the newest .ifc
 * revision first, WHA-3688); the winner is resolved by SOURCE_PRECEDENCE.
 * Exported for unit testing without mocking Supabase.
 */
export function mergeHeaderAttributes(rows: {
  findings: FindingRow[] | null;
  api: ApiRow[] | null;
  ifc: IfcFindingRow[] | null;
  user: UserRow[] | null;
}): BuildingHeaderAttributes {
  type Candidate = {
    value: string | null;
    source: string;
    sourceUser?: AttributeSourceUser | null;
  };
  const candidates = new Map<string, Partial<Record<AttributeSourceKind, Candidate>>>();
  const addCandidate = (key: string, kind: AttributeSourceKind, candidate: Candidate) => {
    const slot = candidates.get(key) ?? {};
    if (slot[kind]) return; // keep-first
    slot[kind] = candidate;
    candidates.set(key, slot);
  };

  // Extractor findings. Stored values stay purely numeric (or canonical for
  // enums) — units are only appended at display time (BuildingHeader). See
  // WHA-3137.
  for (const row of rows.findings ?? []) {
    if (!isHeaderAttribute(row.attribute_nm) || isPlaceholderValue(row.extracted_value)) continue;
    const source = row.source_filenames?.[0] ?? "AI extraction";
    addCandidate(row.attribute_nm, "extractor", {
      value: displayAttributeValue(row.extracted_value.trim()),
      source,
    });
  }

  // API/EHR data.
  for (const row of rows.api ?? []) {
    if (!isHeaderAttribute(row.attribute_key) || isPlaceholderValue(row.attribute_value)) continue;
    const source = row.source_system || "EHR";
    addCandidate(row.attribute_key, "ehr", {
      value: displayAttributeValue(row.attribute_value.trim()),
      source: source.toUpperCase(),
    });
  }

  // IFC / BIM-model findings. get_ifc_findings returns one row per
  // (ifc_document_id, attribute) ordered newest-revision-first, so keep-first
  // gives the newest .ifc's value (WHA-3688).
  for (const row of rows.ifc ?? []) {
    if (!isHeaderAttribute(row.attribute_nm) || isPlaceholderValue(row.extracted_value)) continue;
    // Show the bare .ifc filename (no "BIM model:" prefix) so the source tooltip
    // reads cleanly AND resolves to the document on click (the viewer looks it
    // up by filename → opens the 3D-model confirm).
    const source = row.ifc_filename ?? "BIM model";
    addCandidate(row.attribute_nm, "ifc", {
      value: displayAttributeValue(row.extracted_value.trim()),
      source,
    });
  }

  // User-entered data.
  for (const row of rows.user ?? []) {
    if (!isHeaderAttribute(row.attribute_key)) continue;
    const value = row.attribute_value;
    const name = row.edited_by_name || "User";
    const sourceUser: AttributeSourceUser | null =
      typeof row.edited_by === "number"
        ? {
            id: row.edited_by,
            name,
            avatarUrl: row.edited_by_avatar_url ?? null,
            color: row.edited_by_color ?? null,
            editedAt: row.created_at ?? null,
          }
        : null;
    if (value !== null && value.trim() === "") {
      const rejectedAt = row.created_at ?? null;
      if (!rejectionStillApplies(rejectedAt, machineUpdateTimes(row.attribute_key, rows))) {
        continue;
      }
      addCandidate(row.attribute_key, "user", {
        value: null,
        source: `Edited by: ${name}`,
        sourceUser,
      });
      continue;
    }
    if (value === null || isPlaceholderValue(value)) continue;
    addCandidate(row.attribute_key, "user", {
      value: value.trim(),
      source: `Edited by: ${name}`,
      sourceUser,
    });
  }

  const field = (key: string): AttributeField => {
    const entry = pickByPrecedence(candidates.get(key) ?? {});
    return entry
      ? { value: entry.value, source: entry.source, sourceUser: entry.sourceUser ?? null }
      : { value: null, source: null, sourceUser: null };
  };

  return {
    netArea: field("net_area"),
    floors: field("floors"),
    heatingSource: field("heating_source"),
  };
}

export function useBuildingAttributes(buildingId: string | undefined, orgId: string | undefined) {
  return useQuery({
    queryKey: ["building-header-attributes", buildingId, orgId],
    queryFn: async (): Promise<BuildingHeaderAttributes> => {
      if (!buildingId || !orgId) {
        return mergeHeaderAttributes({ findings: null, api: null, ifc: null, user: null });
      }
      const buildingIdNum = parseInt(buildingId, 10);

      // Query all four sources in parallel
      const [findingsRes, apiRes, ifcRes, userRes] = await Promise.all([
        // AI-extracted findings
        supabase.rpc("get_building_findings", {
          p_org_id: orgId,
          p_building_id: buildingIdNum,
        }),
        // API/EHR data — routed through a public RPC wrapper because
        // data.api_building_data lives in the `data` schema, which is not
        // exposed to PostgREST. See migration 20260413152144.
        supabase.rpc(
          "get_api_building_data" as never,
          { p_org_id: orgId, p_building_id: buildingIdNum } as never
        ),
        // IFC (BIM-model) deterministic findings — data.ifc_attribute_findings
        // via a SECURITY INVOKER RPC (migration 20260610120015, WHA-3561/3686).
        supabase.rpc(
          "get_ifc_findings" as never,
          { p_org_id: orgId, p_building_id: buildingIdNum } as never
        ),
        // User-entered data
        supabase.rpc(
          "get_user_data" as never,
          { p_org_id: orgId, p_building_id: buildingIdNum } as never
        ),
      ]);

      return mergeHeaderAttributes({
        findings: findingsRes.error ? null : (findingsRes.data as unknown as FindingRow[] | null),
        api: apiRes.error ? null : (apiRes.data as unknown as ApiRow[] | null),
        ifc: ifcRes.error ? null : (ifcRes.data as unknown as IfcFindingRow[] | null),
        user: userRes.error
          ? null
          : normalizeHeaderUserRows(userRes.data as unknown as UserRow[] | null),
      });
    },
    enabled: !!buildingId && !!orgId,
    staleTime: 5 * 60 * 1000,
  });
}
